import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../../config";
import RoleDashboard from "./RoleDashboard";
import m from "./LibrarianDashboard.module.css";

/** Stable hue per book title — gives each book a colour-tinted spine
 *  without any extra DB columns. */
function hueFor(seed) {
  const str = String(seed || "•");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % 360;
}
function initials(first, last) {
  const a = (first || "").trim()[0] || "";
  const b = (last  || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}
const STUDENT_PALETTE = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return STUDENT_PALETTE[h % STUDENT_PALETTE.length];
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return "₱" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtRelative(iso) {
  if (!iso) return "";
  const ms  = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return new Date(iso).toLocaleDateString();
  const min = Math.floor(ms / 60000);
  if (min < 1)  return "just now";
  if (min < 60) return `${min}m ago`;
  const hr  = Math.floor(min / 60);
  if (hr  < 24) return `${hr}h ago`;
  const d   = Math.floor(hr / 24);
  if (d   < 7)  return `${d}d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { month: "short", day: "numeric" });
  } catch { return new Date(iso).toLocaleDateString(); }
}

/** Workspace fetch — every endpoint is admin-/librarian-scoped server-
 *  side (the Books API gates writes via AdminGuard; reads are open). We
 *  pull the whole catalog because it's small and powers every tile. */
function useLibrarianData() {
  const [state, setState] = useState({
    loading: true,
    error: "",
    books: [],
    purchases: [],
    requiredBooks: [],
    subjects: [],
    students: [],
    schoolYears: [],
  });

  useEffect(() => {
    let cancelled = false;
    setState(p => ({ ...p, loading: true, error: "" }));
    const safe = (promise, fallback) => promise.then(r => r.ok ? r.json() : fallback).catch(() => fallback);
    Promise.all([
      safe(fetch(`${API_BASE}/api/books`), []),
      safe(fetch(`${API_BASE}/api/student-book-purchases`), []),
      safe(fetch(`${API_BASE}/api/required-books`), []),
      safe(fetch(`${API_BASE}/api/subjects`), []),
      safe(fetch(`${API_BASE}/api/students?all=true`), []),
      safe(fetch(`${API_BASE}/api/school-years`), []),
    ]).then(([books, purchases, requiredBooks, subjects, students, schoolYears]) => {
      if (cancelled) return;
      setState({
        loading: false,
        error: "",
        books, purchases, requiredBooks, subjects, students, schoolYears,
      });
    }).catch(e => {
      if (!cancelled) setState(p => ({ ...p, loading: false, error: e.message || "Failed to load" }));
    });
    return () => { cancelled = true; };
  }, []);

  return state;
}

export default function LibrarianDashboard({ user }) {
  const data = useLibrarianData();
  const navigate = useNavigate();

  const booksById    = useMemo(() => Object.fromEntries(data.books.map(b => [b.id, b])), [data.books]);
  const studentsById = useMemo(() => Object.fromEntries(data.students.map(s => [s.id, s])), [data.students]);

  const activeSy = useMemo(
    () => data.schoolYears.find(y => y.active) || data.schoolYears[0] || null,
    [data.schoolYears]
  );

  // Stats — counts are bare; we also derive a "this term" purchase volume
  // for a quick sense of how active the librarian's desk has been.
  const stats = useMemo(() => {
    const catalog = data.books.length;
    const required = data.requiredBooks.length;
    let recentCount = 0, recentTotal = 0;
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    for (const p of data.purchases) {
      const t = p.purchasedAt ? new Date(p.purchasedAt).getTime() : 0;
      if (t >= thirtyDaysAgo) {
        recentCount++;
        const amt = Number(p.amount);
        if (Number.isFinite(amt)) recentTotal += amt;
      }
    }
    // Subject coverage — how many distinct subjects have at least one
    // required-book entry vs. how many subjects exist. Quick sense of
    // catalogue completeness against the curriculum.
    const subjectsWithRequired = new Set(data.requiredBooks.map(r => r.subjectId)).size;
    return {
      catalog, required, recentCount, recentTotal,
      subjectsWithRequired,
      subjectsTotal: data.subjects.length,
    };
  }, [data.books, data.purchases, data.requiredBooks, data.subjects]);

  // Recent purchases — newest first, top 8 for the card. Each entry
  // carries the friendly labels so the card maps purely on the model
  // without per-row lookups.
  const recentPurchases = useMemo(() => {
    const rows = (data.purchases || []).slice().sort((a, b) => {
      const ta = a.purchasedAt ? new Date(a.purchasedAt).getTime() : 0;
      const tb = b.purchasedAt ? new Date(b.purchasedAt).getTime() : 0;
      return tb - ta;
    }).slice(0, 8);
    return rows.map(p => {
      const book = booksById[p.bookId];
      const stu  = studentsById[p.studentId];
      const studentName = stu
        ? `${stu.lastName || ""}, ${stu.firstName || ""}`.replace(/^, |, $/g, "").trim()
        : `Student #${p.studentId}`;
      return {
        id: p.id,
        bookTitle: book?.title || `Book #${p.bookId}`,
        bookAuthor: book?.author || "",
        studentName,
        studentNumber: stu?.studentNumber || null,
        amount: p.amount,
        purchasedAt: p.purchasedAt,
      };
    });
  }, [data.purchases, booksById, studentsById]);

  // Subject coverage grid — for each subject, how many required-book
  // entries are on file. Helps a librarian spot subjects with no
  // catalogue yet ("Math 7 — 0 books required" alongside healthy ones).
  const subjectCoverage = useMemo(() => {
    const countBySubject = new Map();
    for (const r of data.requiredBooks) {
      countBySubject.set(r.subjectId, (countBySubject.get(r.subjectId) || 0) + 1);
    }
    return data.subjects.map(s => ({
      id: s.id,
      label: s.title || s.code || `Subject #${s.id}`,
      code: s.code || "",
      count: countBySubject.get(s.id) || 0,
    })).sort((a, b) => {
      // Subjects with zero required books surface first — they're the
      // ones the librarian needs to act on.
      if ((a.count === 0) !== (b.count === 0)) return a.count === 0 ? -1 : 1;
      return a.label.localeCompare(b.label);
    }).slice(0, 12);
  }, [data.subjects, data.requiredBooks]);

  const displayName = user?.linkedEntity?.displayName || user?.username || "Librarian";

  return (
    <RoleDashboard
      eyebrow="Librarian"
      title={`Welcome, ${displayName}.`}
      sub={activeSy
        ? `Live library snapshot for ${activeSy.label}. Catalogue, required-books per subject, and recent student purchases — all in one place.`
        : "Live library snapshot. Catalogue, required-books per subject, and recent student purchases — all in one place."}
      user={user}
    >
      {data.loading && (
        <div className={m.skel}>
          <div className={m.skelRow} />
          <div className={m.skelRow} />
          <div className={m.skelRow} />
        </div>
      )}

      {!data.loading && data.error && (
        <div className={m.error}>{data.error}</div>
      )}

      {!data.loading && !data.error && (
        <>
          {/* ── Stat tiles ─────────────────────────────────── */}
          <div className={m.statRow}>
            <Stat
              label="Books in catalogue"
              value={stats.catalog}
              hint={stats.catalog === 0 ? "No books on file yet" : "Total titles on file"}
            />
            <Stat
              label="Required-book entries"
              value={stats.required}
              hint={`${stats.subjectsWithRequired} of ${stats.subjectsTotal} subjects covered`}
              tone={stats.subjectsWithRequired === stats.subjectsTotal && stats.subjectsTotal > 0 ? "good" : "warn"}
            />
            <Stat
              label="Purchases · 30 days"
              value={stats.recentCount}
              hint={stats.recentCount > 0 ? fmtMoney(stats.recentTotal) + " collected" : "No purchases recorded"}
              tone={stats.recentCount > 0 ? "info" : "none"}
            />
            <Stat
              label="Active school year"
              value={activeSy?.label || "—"}
              hint={activeSy ? "Used for required-books filter" : "Set an active SY"}
              tone={activeSy ? "none" : "warn"}
            />
          </div>

          {/* ── Recent purchases ───────────────────────────── */}
          <section className={m.section}>
            <header className={m.sectionHead}>
              <div>
                <h3 className={m.sectionTitle}>Recent purchases</h3>
                <p className={m.sectionSub}>
                  Newest 8 book purchases — students who've checked in to
                  buy a required book from the library.
                </p>
              </div>
              <button type="button" className={m.linkBtn}
                      onClick={() => navigate("/admin/books")}>
                Manage catalogue →
              </button>
            </header>

            {recentPurchases.length === 0 ? (
              <div className={m.empty}>
                No purchases recorded yet. Once students start buying their
                required books, the latest sales will appear here.
              </div>
            ) : (
              <ul className={m.list}>
                {recentPurchases.map((p, idx) => {
                  const hue = hueFor(p.bookTitle);
                  return (
                    <li
                      key={p.id}
                      className={m.row}
                      style={{ "--hue": hue, animationDelay: `${Math.min(idx, 8) * 35}ms` }}>
                      <div className={m.spine} aria-hidden="true" />
                      <div className={m.rowBody}>
                        <div className={m.rowMain}>
                          <div className={m.bookTitle} title={p.bookTitle}>
                            {p.bookTitle}
                          </div>
                          {p.bookAuthor && (
                            <div className={m.bookAuthor}>by {p.bookAuthor}</div>
                          )}
                        </div>
                        <div className={m.studentChip}>
                          <span className={m.studentAvatar}
                                style={{ background: avatarColor(p.studentName) }}>
                            {initials(p.studentName.split(",")[1] || "", p.studentName.split(",")[0] || "")}
                          </span>
                          <div className={m.studentBody}>
                            <div className={m.studentName} title={p.studentName}>{p.studentName}</div>
                            {p.studentNumber && (
                              <div className={m.studentNumber}>#{p.studentNumber}</div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className={m.rowMeta}>
                        <div className={m.amount}>{fmtMoney(p.amount)}</div>
                        <div className={m.when} title={p.purchasedAt
                            ? new Date(p.purchasedAt).toLocaleString() : ""}>
                          {fmtRelative(p.purchasedAt)}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* ── Subject coverage ───────────────────────────── */}
          {subjectCoverage.length > 0 && (
            <section className={m.section}>
              <header className={m.sectionHead}>
                <div>
                  <h3 className={m.sectionTitle}>Subjects · required-book coverage</h3>
                  <p className={m.sectionSub}>
                    Subjects with <strong>0 required books</strong> show up first
                    — those are the gaps to fill before the term starts.
                  </p>
                </div>
                <button type="button" className={m.linkBtn}
                        onClick={() => navigate("/admin/books")}>
                  Add required books →
                </button>
              </header>
              <div className={m.coverageGrid}>
                {subjectCoverage.map((sc, idx) => (
                  <article
                    key={sc.id}
                    className={`${m.coverCard} ${sc.count === 0 ? m.coverCardEmpty : ""}`}
                    style={{ animationDelay: `${Math.min(idx, 10) * 30}ms` }}>
                    <div className={m.coverHead}>
                      <div className={m.coverIcon}
                           style={{ "--hue": hueFor(sc.code || sc.label) }}
                           aria-hidden="true">
                        {(sc.code || sc.label).slice(0, 2).toUpperCase()}
                      </div>
                      <div className={m.coverIdent}>
                        <div className={m.coverTitle} title={sc.label}>{sc.label}</div>
                        {sc.code && <div className={m.coverCode}>{sc.code}</div>}
                      </div>
                    </div>
                    <div className={m.coverCount}>
                      <span className={m.coverCountNum}>{sc.count}</span>
                      <span className={m.coverCountLbl}>
                        required book{sc.count === 1 ? "" : "s"}
                      </span>
                    </div>
                    {sc.count === 0 && (
                      <span className={m.coverBadge}>Needs attention</span>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </RoleDashboard>
  );
}

function Stat({ label, value, hint, tone }) {
  return (
    <div className={`${m.stat} ${tone ? m["stat_" + tone] : ""}`}>
      <div className={m.statLabel}>{label}</div>
      <div className={m.statValue}>{value ?? "—"}</div>
      <div className={m.statHint} title={hint}>{hint}</div>
    </div>
  );
}
